// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: a bookmark's note under its message in the bookmarks list (the page, the phone-width sheet and the
// desktop inbox popout; favorites-bookmarks-plan.md sections 1.4, 1.5 and 4): the note when there is one, "Add note"
// or "Edit note", and an inline editor with Save and Cancel. Events stay inside, so typing or selecting here never
// jumps to the message, opens its menu or moves the list's keyboard focus. Exclude from upstream sync.

import * as BookmarkNoteCommands from '@app/features/messaging/commands/BookmarkNoteCommands';
import styles from '@app/features/messaging/components/BookmarkNote.module.css';
import {
	NOTE_CANCEL_DESCRIPTOR,
	NOTE_SAVE_DESCRIPTOR,
	FavoritesBookmarksStrings as S,
} from '@app/features/messaging/FavoritesBookmarksStrings';
import BookmarkNotes from '@app/features/messaging/state/BookmarkNotes';
import {Button, ButtonVariant} from '@app/features/ui/button/Button';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useRef, useState} from 'react';

const keepInside = (event: React.SyntheticEvent) => event.stopPropagation();

export const BookmarkNote = observer(({messageId, 'data-flx': dataFlx}: {messageId: string; 'data-flx'?: string}) => {
	const {i18n} = useLingui();
	const note = BookmarkNotes.get(messageId);
	const [draft, setDraft] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const editorRef = useRef<HTMLTextAreaElement | null>(null);
	const editing = draft !== null;

	useEffect(() => {
		if (editing) editorRef.current?.focus();
	}, [editing]);

	const save = async () => {
		if (draft === null || saving) return;
		setSaving(true);
		const saved = await BookmarkNoteCommands.setNote(messageId, draft);
		setSaving(false);
		if (saved) setDraft(null);
	};

	return (
		<div
			role="group"
			className={styles.bookmarkNote}
			onClick={keepInside}
			onPointerDown={keepInside}
			onKeyDown={keepInside}
			onContextMenu={keepInside}
			data-flx={dataFlx ?? 'messaging.bookmark-note'}
		>
			{editing ? (
				<div className={styles.editor} data-flx="messaging.bookmark-note.editor">
					<textarea
						ref={editorRef}
						className={styles.textarea}
						aria-label={S.note}
						value={draft}
						rows={3}
						disabled={saving}
						onChange={(event) => setDraft(event.target.value)}
						data-flx="messaging.bookmark-note.textarea"
					/>
					<div className={styles.editorActions} data-flx="messaging.bookmark-note.editor-actions">
						<Button
							small
							variant={ButtonVariant.SECONDARY}
							disabled={saving}
							onClick={() => setDraft(null)}
							data-flx="messaging.bookmark-note.cancel"
						>
							{i18n._(NOTE_CANCEL_DESCRIPTOR)}
						</Button>
						<Button small submitting={saving} onClick={save} data-flx="messaging.bookmark-note.save">
							{i18n._(NOTE_SAVE_DESCRIPTOR)}
						</Button>
					</div>
				</div>
			) : (
				<>
					{note !== '' && (
						<p className={styles.noteText} data-flx="messaging.bookmark-note.text">
							{note}
						</p>
					)}
					<button
						type="button"
						className={styles.noteAction}
						onClick={() => setDraft(note)}
						data-flx="messaging.bookmark-note.open-editor"
					>
						{note === '' ? S.addNote : S.editNote}
					</button>
				</>
			)}
		</div>
	);
});
